/**
 * 断线状态条：顶栏下方一条 28px 的**非阻塞**横幅（§11.6）。
 *
 * 三条设计约束，每一条都来自「弱网下真实的烦人程度」：
 *
 *  1. **短断线不闪**。3 秒内自愈的抖动玩家根本没察觉，弹一下反而制造焦虑；
 *     所以进入非 live 相位后要先等 `OFFLINE_FLASH_GUARD_MS` 才允许显示。
 *  2. **恢复后只说有用的**。「重连成功」不是消息；「你错过了 3 手牌」才是。
 *     没错过任何手牌时静默清掉 resume 摘要，不打扰。
 *  3. **绝不用全屏模态挡牌桌**。断线时牌局还在走，挡住牌桌等于让人连输在哪都看不见。
 *
 * ★ 红线 #48：重试倒计时直接写 DOM 节点，不进 React state。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { backoffDelayMs } from '../transport/backoff.js'
import {
  selectNetPhase,
  selectResume,
  useNetStore,
} from '../transport/applyMessage.js'

import {
  OFFLINE_FLASH_GUARD_MS,
  RESUME_HINT_MS,
  resolveConnectionBar,
  type ConnectionBarModel,
} from './roomDerive.js'
import { settlementOf } from './topUpDerive.js'
import { useRoomLink } from './roomLink.js'
import './room.css'

export interface ConnectionBarProps {
  /** 房间已关闭 / 会话失效时的去向（一般是「回大厅」）。 */
  readonly onLeave?: () => void
}

export function ConnectionBar({ onLeave }: ConnectionBarProps): ReactNode {
  const phase = useNetStore(selectNetPhase)
  const fatal = useNetStore((s) => s.fatal)
  const resume = useNetStore(selectResume)
  const clearResume = useNetStore((s) => s.clearResume)
  const settled = useNetStore((s) => settlementOf(s.view) !== null)
  const link = useRoomLink()

  /** 进入非 live 相位的时刻。用 state 而不是 ref：它要参与渲染判定。 */
  const [offlineSince, setOfflineSince] = useState<number | null>(null)
  const offlineSinceRef = useRef<number | null>(null)
  /** 纯粹用来在 3 秒守卫到期时重新求值一次，本身没有语义 */
  const [, bump] = useState(0)
  const countdownRef = useRef<HTMLSpanElement | null>(null)

  const online = phase === 'live' || phase === 'idle'

  useEffect(() => {
    if (online) {
      offlineSinceRef.current = null
      setOfflineSince(null)
      return undefined
    }
    // 已经在计时就别重置：connecting → backoff → connecting 的往复不该让守卫一直归零，
    // 否则弱网下疯狂重试时这条横幅永远等不到 3 秒、永远不显示。
    if (offlineSinceRef.current === null) {
      offlineSinceRef.current = Date.now()
      setOfflineSince(offlineSinceRef.current)
    }
    const t = setTimeout(() => bump((n) => n + 1), OFFLINE_FLASH_GUARD_MS)
    return () => clearTimeout(t)
  }, [online])

  const model: ConnectionBarModel = resolveConnectionBar({
    phase,
    fatal,
    resume,
    offlineSinceMs: offlineSince,
    now: Date.now(),
    exhausted: link.backoff?.exhausted(Date.now()) ?? false,
    settled,
  })

  // 「你错过了 N 手牌」展示 3 秒后自己消失：它是一条通知，不是一个需要确认的状态
  useEffect(() => {
    if (model.kind !== 'resumed') return undefined
    const t = setTimeout(() => clearResume(), RESUME_HINT_MS)
    return () => clearTimeout(t)
  }, [model.kind, clearResume])

  // 没错过手牌的重连摘要静默清掉，免得它一直挂在 store 里，下次真错过时被当成旧数据
  useEffect(() => {
    if (resume !== null && resume.missedHands <= 0) clearResume()
  }, [resume, clearResume])

  // —— 重试倒计时：写 DOM，不走 state（红线 #48）——
  // 退避里的 jitter 是随机的，客户端拿不到确切的下次重试时刻，所以文案写「约」。
  useEffect(() => {
    if (!model.showCountdown) return undefined
    const attempt = link.backoff?.attempt() ?? 0
    // fail() 先返回延迟再自增，所以「当前正在等的那次」用的是 attempt - 1
    const estimate = backoffDelayMs(Math.max(0, attempt - 1), () => 0.5)
    const endsAt = Date.now() + estimate
    const write = (): void => {
      const el = countdownRef.current
      if (el === null) return
      const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
      el.textContent = left > 0 ? `约 ${left}s 后重试` : '正在重试…'
    }
    write()
    const timer = setInterval(write, 250)
    return () => clearInterval(timer)
  }, [model.showCountdown, link.backoff, phase])

  const retry = useCallback((): void => {
    link.retryNow()
  }, [link])

  if (model.kind === 'none') return null

  return (
    <div className={`connbar connbar--${model.kind}`} role="status" aria-live="polite">
      <span className="connbar__text">{model.text}</span>
      {model.sub !== null && <span className="connbar__sub">{model.sub}</span>}
      {model.showCountdown && <span className="connbar__sub num" ref={countdownRef} />}
      {model.showRetry && (
        <button type="button" className="btn btn--bar" onClick={retry}>
          立即重试
        </button>
      )}
      {model.kind === 'fatal' && onLeave !== undefined && (
        <button type="button" className="btn btn--bar" onClick={onLeave}>
          回大厅
        </button>
      )}
    </div>
  )
}

export default ConnectionBar
